<template>
  <li class="cn-header-menu-item">
    <div ref="menuLink" class="cn-header-menu-item__wrapper">
      <UiLink
        :to="menuItem.url"
        class="cn-header-menu-item__link"
        :class="{ 'cn-header-menu-item__link--hover': isActive }"
      >
        <span>{{ menuItem.name }}</span>
        <UiIcon v-if="hasChildren" name="chevron-down" />
      </UiLink>
      <transition
        enter-active-class="transform duration-300"
        enter-from-class="opacity-0 -translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transform duration-300"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-4"
      >
        <div
          v-if="hasChildren"
          v-show="isActive"
          class="cn-header-menu-item__submenu"
        >
          <div class="container">
            <div class="cn-header-menu-item__submenu-grid">
              <div
                v-for="category in menuItem.children"
                :key="category.name"
                class="cn-header-menu-item__submenu-category"
              >
                <CdnImg
                  v-if="category.image"
                  provider="imgproxy"
                  :src="category.image.path"
                  format="webp"
                  :width="28"
                  :height="28"
                />
                <div class="cn-header-menu-item__submenu-category-wrapper">
                  <UiLink
                    :to="category.url"
                    class="cn-header-menu-item__submenu-category-title"
                  >
                    <span>{{ category.name }}</span>
                  </UiLink>
                  <UiLink
                    v-for="subcategory in category.children"
                    :key="subcategory.name"
                    :to="subcategory.url"
                    class="cn-header-menu-item__submenu-category-link"
                  >
                    {{ subcategory.name }}
                  </UiLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </li>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useElementHover } from '@vueuse/core';
import { CdnImg } from '@webplatform/cdn-image';
import UiIcon from '@/components/ui/UiIcon';
import UiLink from '@/components/ui/UiLink';

interface Props {
  menuItem: any;
  isActive?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'set-active-item'): void;
}>();

const menuLink = ref();
const isHoveredLink = useElementHover(menuLink);

const hasChildren = computed(
  () => props.menuItem.children && props.menuItem.children.length,
);

watch(isHoveredLink, (value) => {
  if (value === true) emit('set-active-item');
});
</script>

<style lang="scss">
.cn-header-menu-item {
  --ab-canyon-header-menu-item-link-color-text: var(--ab-canyon-color-dark);
  --ab-canyon-header-menu-item-link-color-text-hover: var(
    --ab-canyon-color-accent
  );
  --ab-canyon-header-menu-item-submenu-color-bg: var(--ab-canyon-color-light);
  --ab-canyon-header-menu-item-submenu-category-title-color-text: var(
    --ab-canyon-color-dark
  );
  --ab-canyon-header-menu-item-submenu-category-title-color-text-hover: var(
    --ab-canyon-color-accent
  );
  --ab-canyon-header-menu-item-submenu-category-link-color-text: var(
    --ab-canyon-color-dark
  );
  --ab-canyon-header-menu-item-submenu-category-link-color-text-hover: var(
    --ab-canyon-color-accent
  );

  padding-left: var(--size-xs);
  padding-right: var(--size-xs);
  align-items: center;
  height: 100%;
  display: flex;
  &__link {
    display: block;
    color: var(--ab-canyon-header-menu-item-link-color-text);
    transition-property: color;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
    @media (min-width: 1656px) {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }

    .cn-icon {
      display: inline-block;
      width: var(--size-md);
      height: var(--size-md);
      transition-property: transform;
      transition-duration: 150ms;
    }

    &--hover {
      color: var(--ab-canyon-header-menu-item-link-color-text-hover);
      .cn-icon {
        transform: rotate(180deg);
      }
    }
  }
  &__submenu {
    background-color: var(--ab-canyon-header-menu-item-submenu-color-bg);
    box-shadow:
      0 0 #00000000,
      0 0 #00000000,
      0 4px 6px -1px #0000001a,
      0 2px 4px -2px #0000001a;
    top: 100%;
    right: 0px;
    left: 0px;
    position: absolute;
  }
  &__submenu-grid {
    padding-top: var(--size-3xl);
    padding-bottom: var(--size-3xl);
    gap: var(--size-xl);
    grid-template-columns: repeat(4, minmax(0, 1fr));
    display: grid;
  }
  &__submenu-category {
    align-items: flex-start;
    display: flex;
    > img {
      margin-right: calc(var(--size-base) / 2);
    }
  }
  &__submenu-category-icon {
    color: var(--ab-canyon-header-menu-item-submenu-category-icon-color);
  }
  &__submenu-category-title {
    color: var(--ab-canyon-header-menu-item-submenu-category-title-color-text);
    font-weight: 700;
    display: block;
    @media (min-width: 1656px) {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    &:hover {
      color: var(
        --ab-canyon-header-menu-item-submenu-category-title-color-text-hover
      );
      text-decoration: underline;
    }
  }
  &__submenu-category-link {
    color: var(--ab-canyon-header-menu-item-submenu-category-link-color-text);

    display: block;
    margin-top: calc(var(--size-base) / 2);
    @media (min-width: 1656px) {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    &:hover {
      color: var(
        --ab-canyon-header-menu-item-submenu-category-link-color-text-hover
      );
      text-decoration: underline;
    }
  }
}
</style>
